import { appendLobbyReturnToPlayUrl } from '@low6-pickem/frontend-shared';
import { extractTokenFromUrl, getStoredToken } from './auth';

/**
 * DK's deep-link param. Mirrors GAME_QUERY_PARAM in frontend-shared/tenant-resolver, which is
 * not re-exported from that package's index; kept local rather than widening the shared API and
 * re-vendoring every app for one string. The name is a fixed contract on DK's side.
 */
const GAME_QUERY_PARAM = 'gameId';

/**
 * The DK JWT to forward to a game.
 *
 * The URL copy wins. DK's token lives ~300s and they mint a fresh one each time they load the
 * frame, so the query string always holds the newest one; sessionStorage may still hold a token
 * captured on an earlier frame load in the same tab. Forwarding that stale copy would land the
 * viewer on a 401 and an OAuth redirect DK's iframe blocks.
 *
 * Stored is the fallback, for the click path once a non-iframe visit has stripped the param.
 */
function tokenToForward(): string | null {
  return extractTokenFromUrl() ?? getStoredToken();
}

/**
 * Drop DK's deep-link `gameId` from a lobby URL.
 *
 * Anything that sends a viewer BACK to the lobby must not carry the param that sent them out of
 * it. `lobbyReturn` preserves the query string, so a hand-off that returned its own entry URL
 * would bounce the viewer straight back into the game — and the games cache `lobbyReturn` in
 * sessionStorage on first land, so the trap would outlive the URL that caused it.
 *
 * Every other param stays: `tenantId` and `token` are how the lobby identifies and authenticates
 * the viewer on the way back.
 */
export function stripDeepLinkGameId(href: string): string {
  try {
    const url = new URL(href, window.location.origin);
    if (!url.searchParams.has(GAME_QUERY_PARAM)) return href;
    url.searchParams.delete(GAME_QUERY_PARAM);
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * Build the URL to send a viewer to a game: the game's public URL plus the lobby return path
 * and the viewer's token.
 *
 * Shared by the Play Now click path and the deep-link hand-off so a game is entered exactly
 * one way, with the same params, however the viewer got there.
 */
export function buildPlayUrl(publicUrl: string | null): string | null {
  if (!publicUrl) return null;
  // Pass full lobby URL so games can navigate back to /tenant/{code}/lobby/{slug}.
  const url = appendLobbyReturnToPlayUrl(publicUrl);
  const token = tokenToForward();
  if (!token) return url;
  return `${url}&token=${encodeURIComponent(token)}`;
}

/**
 * Deep-link a play URL straight into the game's picks-editing UI.
 *
 * Used by cards that offer "Edit Picks": without it the pick'em app opens on its My Picks
 * summary, which only carries a second Edit Picks button — two taps to reach the selections
 * the player already asked to edit. The pick'em app reads `edit=1` in CompetitionView.
 */
export function appendEditPicksParam(playUrl: string | null): string | null {
  if (!playUrl) return null;
  const [base, hash = ''] = playUrl.split(/(#.*)$/, 2);
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}edit=1${hash}`;
}
